import {
    Body1Strong,
    Button,
    Title2
} from "@fluentui/react-components";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import "../../styles/prism-material-oceanic.css";
import "./../../styles/Chat.css";
import "./../../styles/HomeInput.css";

import { HomeInputProps, QuickTask, quickTasks } from "../../models/homeInput";
import { NewTaskService } from "../../services/NewTaskService";

import PromptCard from "@/coral/components/PromptCard";
import { Send } from "@/coral/imports/bundleicons";
import ChatInput from "@/coral/modules/ChatInput";
import FileUploadButton from "../FileUploadButton";
import InlineToaster, { useInlineToaster } from "../toast/InlineToaster";

const HomeInput: React.FC<HomeInputProps> = ({
    onInputSubmit,
    onQuickTaskSelect,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [input, setInput] = useState("");
    const sessionIdRef = useRef<string | null>(null);

    // Generate session ID once on component mount for file uploads
    useEffect(() => {
        if (!sessionIdRef.current) {
            sessionIdRef.current = `session-${Date.now()}`;
        }
    }, []);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const navigate = useNavigate();
    const location = useLocation(); // ✅ location.state used to control focus
    const { showToast, dismissToast } = useInlineToaster();

    useEffect(() => {
        if (location.state?.focusInput) {
            textareaRef.current?.focus();
        }
    }, [location]);

    const resetTextarea = () => {
        setInput("");
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.focus();
        }
    };

    useEffect(() => {
        const cleanup = NewTaskService.addResetListener(resetTextarea);
        return cleanup;
    }, []);

    const handleSubmit = async () => {
        if (input.trim()) {
            const userMessage = input.trim();
            setSubmitting(true);

            try {
                // Use existing session if file was uploaded, or generate new one
                const sessionId = sessionIdRef.current || `session-${Date.now()}`;

                // Clear input immediately
                setInput("");
                if (textareaRef.current) {
                    textareaRef.current.style.height = "auto";
                }

                // Navigate to conversation view - it will send the message and await the response
                navigate(`/conversation/${sessionId}`, {
                    state: {
                        initialMessage: userMessage,
                        isNewConversation: true
                    }
                });

            } catch (error: any) {
                const errorMessage = error?.message ?
                    (typeof error.message === 'string' ? error.message : JSON.stringify(error.message))
                    : "Failed to send message";
                showToast(errorMessage, "error");
                setInput(userMessage); // Restore input on error
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleQuickTaskClick = (task: QuickTask) => {
        setInput(task.description);
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
        onQuickTaskSelect(task.description);
    };

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [input]);

    return (
        <div className="home-input-container">
            <div className="home-input-content">
                <div className="home-input-center-content">
                    <div className="home-input-title-wrapper">
                        <Title2>How can I help?</Title2>
                    </div>

                    <ChatInput
                        ref={textareaRef} // forwarding
                        value={input}
                        placeholder="Ask about Azure, GCP or AWS access, subscriptions, network setup, or cloud architecture..."
                        onChange={setInput}
                        onEnter={handleSubmit}
                        disabledChat={submitting}
                    >
                        <FileUploadButton
                            sessionId={sessionIdRef.current || `session-${Date.now()}`}
                            onUploadSuccess={(fileInfo) => {
                                // Use the session_id from the upload response (where the file was actually uploaded)
                                const uploadSessionId = fileInfo.session_id || sessionIdRef.current || `session-${Date.now()}`;
                                console.log("Upload success, navigating to session:", uploadSessionId);
                                showToast("Analyzing architecture document...", "success");

                                // Navigate to conversation - analysis is being processed on backend
                                setTimeout(() => {
                                    navigate(`/conversation/${uploadSessionId}`, {
                                        state: {
                                            uploadedFile: fileInfo,
                                            isNewConversation: true
                                        }
                                    });
                                }, 1500);
                            }}
                            onUploadError={(error) => {
                                showToast(`Upload failed: ${error}`, "error");
                            }}
                            disabled={submitting}
                        />
                        <Button
                            appearance="primary"
                            onClick={handleSubmit}
                            disabled={submitting}
                            icon={<Send />}
                        />
                    </ChatInput>

                    <InlineToaster />

                    <div className="home-input-quick-tasks-section">
                        <div className="home-input-quick-tasks-header">
                            <Body1Strong>Example questions</Body1Strong>
                        </div>

                        <div className="home-input-quick-tasks">
                            {quickTasks.map((task) => (
                                <PromptCard
                                    key={task.id}
                                    title={task.title}
                                    icon={task.icon}
                                    description={task.description}
                                    onClick={() => handleQuickTaskClick(task)}
                                    disabled={submitting}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomeInput;
